import { useCallback, useEffect, useRef, useState } from "react";
import { Button, Grid, TextField, Typography } from "@mui/material";
import { Form, Formik, FormikState } from "formik";
import { useSnackbar } from "components/AlertMessages/SnackbarContext";
import axios from "../../api";
import FormatConversationHistory from "./FormatConversationHistory";
import ConversationHistory from "./ConversationHistory";
import DateFormat from "./DateFormat";
import { useParams } from "react-router-dom";
import PageLoader from "./PageLoader";
import { io, Socket } from "socket.io-client";
interface User {
  Role: string | string[];
  name: string;
  userId: number;
  [key: string]: any;
}

interface Message {
  id: string;
  sender_id: number;
  sender_role: string;
  sender_name: string;
  text: string;
  date: string;
}

interface CustomerComment {
  id?: string;
  message: Message;
}
interface Media {
  multimedia_id: number;
  uploaded_by: string;
  media_stage: string | null;
  file_name: string;
  file_type: string;
  file_path: string;
  latitude: number;
  longitude: number;
}
interface Ratings {
  rating_id: number;
  rating: number | null;
  feedback: string | null;
}
interface ConversationData {
  status_id: number;
  customer_comments: string;
  multimedia?: Media[] | null;
  title: string;
  status_tracker: string[];
  category_name: string;
  asset_name: string;
  customer_type: string;
  customer_name: string;
  division_name: string;
  customer_phone: string;
  customer_email: string;
  employee_name: string;
  status_name: string;
  address: string;
  city_name: string;
  state_name: string;
  address_type: string;
  description: string;
  rating: Ratings[];
  created_at: string | Date;
  feedback: string;
  customer_reject_reason: string;
  pending_reason: string;
  ticket_id: number;
}

interface AddConversationProps {
  data: ConversationData;
  user: User;
}
interface Ticket {
  ticket_id: number;
}
const AddConversation: React.FC<AddConversationProps> = ({ data, user }) => {
  const { id } = useParams();
  const currentTime = DateFormat();
  const { fetchError } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [conversationData, setConversationData] = useState<CustomerComment[]>(
    JSON.parse(data.customer_comments || "[]")
  );

  const audioRef = useRef(new Audio("/sounds/notification.mp3"));
  const socket = useRef<Socket | null>(null);

  // Helper function to normalize Role to array
  const getRoleArray = (role: string | string[]): string[] => {
    return Array.isArray(role) ? role : [role];
  };

  // Helper function to get first role as string
  const getFirstRole = (role: string | string[]): string => {
    return Array.isArray(role) ? role[0] : role;
  };

  // Use same origin as API so socket connects to the backend (not wrong port/host)
  const getSocketUrl = () => {
    if (typeof globalThis.location === "undefined") return "";
    const base = axios.defaults.baseURL ?? "/api";
    try {
      const url = new URL(base, globalThis.location.origin);
      return url.origin;
    } catch {
      return globalThis.location.origin;
    }
  };

  // Setup socket.io connection and join this ticket's room
  const setupSocketIO = useCallback(() => {
    if (!id) return () => {};
    const socketUrl = getSocketUrl();
    socket.current = io(socketUrl, {
      path: "/api/socket.io",
      transports: ["websocket", "polling"],
      withCredentials: true,
    });

    socket.current.on("connect", () => {
      socket.current?.emit("join_ticket", id);
    });

    socket.current.on("message", (payload: any) => {
      try {
        const ticketId = payload?.ticket_id ?? payload?.ticketId;
        if (ticketId != null && String(ticketId) !== String(id)) return;
        const message = payload?.message ?? payload;
        const comment = message?.message ? { message: message.message } : { message };
        setConversationData((prevData) => [...prevData, comment]);
        if (audioRef.current) {
          audioRef.current.play().catch((error) => {
            console.error("Audio play error:", error);
          });
        }
      } catch (error) {
        console.error("Error processing message", error);
      }
    });

    return () => {
      socket.current?.disconnect();
      socket.current = null;
    };
  }, [id]);

  useEffect(() => {
    const cleanup = setupSocketIO();
    return cleanup;
  }, [setupSocketIO]);

  // Fetch ticket data from the API
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const ticketResponse = await axios.get(`/tickets/${id}`);
      const { data } = ticketResponse;
      setTicket(data?.list);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      console.error("Error fetching data", err);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleConversation = async (
    values: { customer_comments: any },
    {
      resetForm,
    }: {
      resetForm: (
        nextState?:
          | Partial<FormikState<{ customer_comments: string }>>
          | undefined
      ) => void;
    }
  ) => {
    const conversationId = `conv-${Date.now()}`;
    const message = {
      id: conversationId,
      text: values.customer_comments,
      sender_id: user?.userId,
      sender_role: getFirstRole(user?.Role), // Convert to string
      sender_name: user?.name,
      date: currentTime,
    };

    // Optimistically update UI
    setConversationData((prevData) => [...prevData, { message }]);
    const conversationIdNum = Number(conversationId);

    try {
      const ticketData = {
        customer_comments: ConversationHistory(
          JSON.stringify(conversationData),
          values.customer_comments,
          conversationIdNum,
          user?.userId,
          getRoleArray(user?.Role), // Convert to array
          user?.name
        ),
      };

      // Save the message to the database
      await axios.put(`/tickets/${ticket?.ticket_id}`, { ticketData });
      // Broadcast the message via socket.io to this ticket's room so customer gets it in real time
      const ticketId = ticket?.ticket_id ?? id;
      socket.current?.emit("message", { ...message, ticket_id: ticketId });

      resetForm();
    } catch (err) {
      fetchError(
        (typeof err === "object" &&
          err !== null &&
          "response" in err &&
          (err as any).response?.data?.error) ||
          "An error occurred"
      );

      // Revert optimistic UI update on error
      setConversationData((prevData) =>
        prevData.filter(//NOSONAR
          (msg) => (msg.message?.id ?? (msg as any).id) !== conversationId//NOSONAR
        )
      );
    }
  };
  const normalizedUser = {
    ...user,
    Role: getRoleArray(user?.Role), // Always ensure Role is an array
  };

  return loading ? (
    <PageLoader />
  ) : (
    <>
      <Typography variant="h5">
        {FormatConversationHistory(
          JSON.stringify(conversationData),
          normalizedUser
        )}
      </Typography>
      {[2, 3, 5].includes(data?.status_id) && (
        <Formik
          initialValues={{ customer_comments: "" }}
          onSubmit={(values, { resetForm }) => {
            handleConversation(values, { resetForm });
          }}
        >
          {({ values, handleChange, handleSubmit }) => (
            <Form
              className="add-comment"
              style={{ display: "flex", flexDirection: "column", gap: "20px" }}
              onSubmit={handleSubmit}
            >
              <Grid
                item
                xs={12}
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                }}
              >
                <TextField
                  className="multiline-text"
                  variant="outlined"
                  fullWidth
                  name="customer_comments"
                  value={values.customer_comments}
                  onChange={handleChange}
                />
                <Button
                  sx={{ height: "45px" }}
                  className="success-btn"
                  variant="contained"
                  color="success"
                  type="submit"
                  disabled={!values.customer_comments}
                >
                  Add
                </Button>
              </Grid>
            </Form>
          )}
        </Formik>
      )}
    </>
  );
};

export default AddConversation;
